import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Drawer, Head, SaveView, sortOf, useAdmin, useDrawerRoute, useFilters } from '../ui/Shell';
import { Block, Cell, DateRange, Grid, ListMore, Table, type Row } from '../ui/Block';
import { call, fmtDate } from '../lib/api';
import { describeErr, hhmmss } from '../lib/hooks';
import { countOf, qs, useDebounced, usePagedList, type Paged } from '../lib/list';
import { downloadText } from './Analytics';

interface Event { id: string; action: string; targetType: string | null; targetId: string | null; metadata: unknown; at: string; ip: string | null; userAgent: string | null; staff: boolean; actor: string | null; principalId: string | null; env: string | null }

const GROUPS = ['machine', 'recording', 'broadcast', 'transcription', 'job', 'user', 'release', 'api_key', 'settings'];

function useSearch(value: string, write: (v: string) => void): [string, (v: string) => void] {
  const [q, setQ] = useState(value);
  const dq = useDebounced(q.trim(), 300);
  useEffect(() => { if (dq !== value) write(dq); }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setQ((c) => (c.trim() === value ? c : value)); }, [value]);
  return [q, setQ];
}

export function Audit() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [f, set] = useFilters({ q: '', staff: '', group: '', principal: '', from: '', to: '', sort: '', dir: '' });
  const s = sortOf(f, set, { key: 'at', dir: 'desc' });
  const [q, setQ] = useSearch(f.q, (v) => set({ q: v }));
  const [principal, setPrincipal] = useSearch(f.principal, (v) => set({ principal: v }));
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [exporting, setExporting] = useState(false);

  const filters = { q: f.q, staff: f.staff === 'true', group: f.group, principal: f.principal, from: f.from, to: f.to, sort: f.sort, dir: f.sort ? s.sort.dir : '' };
  const path = `/admin/audit${qs(filters)}`;
  const l = usePagedList<Event, Paged & { events: Event[] }>(env, path, 'events', 30, paused);
  const events = l.rows;

  const csv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      downloadText(`audit-log-${new Date().toISOString().slice(0, 10)}.csv`, await call<string>(env, `/admin/audit${qs({ ...filters, format: 'csv' })}`, { raw: true }));
      ctx.toast(l.total !== null ? `Exported ${Math.min(l.total, 50_000).toLocaleString()} events` : 'Exported the audit log', 'Every event matching the filters (50,000 at most)', { logged: false });
    } catch (e) {
      ctx.toast('Not exported', e instanceof Error ? e.message : String(e), { logged: false });
    } finally {
      setExporting(false);
    }
  };

  const metaOf = (e: Event) => JSON.stringify({ environment: e.env ?? env.key, principalId: e.principalId, ...(typeof e.metadata === 'object' && e.metadata ? (e.metadata as object) : { metadata: e.metadata }) }, null, 2);
  const drawer = (e: Event) => <Drawer eyebrow="AUDIT EVENT" title={e.action} mono sub={`${e.actor ?? 'no actor'} · ${fmtDate(e.at)}`}><pre className="tbl__meta" style={{ margin: 0 }}>{metaOf(e)}</pre></Drawer>;
  useDrawerRoute('audit', (id) => { if (!l.data) return undefined; const e = events.find((x) => x.id === id); return e ? drawer(e) : null; }, [l.data, events]);
  const linked = useRef<string | null>(null);
  useEffect(() => {
    const id = ctx.params.id;
    if (!id || !l.data || linked.current === id) return;
    linked.current = id;
    const e = events.find((x) => x.id === id);
    if (e) ctx.openDrawer(`audit:${e.id}`, drawer(e));
    else ctx.toast('That event is not in the loaded rows', 'Clear the filters or load more to find it', { logged: false });
    const { id: _, ...rest } = ctx.params;
    ctx.setParams(rest);
  }, [ctx.params.id, l.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows: Row[] = events.map((e) => ({
    key: e.id,
    expanded: !!open[e.id],
    meta: metaOf(e),
    onClick: mobile
      ? () => ctx.openDrawer(`audit:${e.id}`, drawer(e))
      : () => setOpen((o) => ({ ...o, [e.id]: !o[e.id] })),
    cells: [
      { t: fmtDate(e.at), mono: true },
      { t: e.action, mono: true, fw: 600 },
      { t: e.actor ?? '—', sub: e.staff ? 'staff' : undefined, fg: e.actor ? undefined : 'var(--ink-40)' },
      { t: e.targetId ?? '—', sub: e.targetType ?? undefined, mono: true, fg: e.targetId ? undefined : 'var(--ink-40)' },
      { t: e.env ?? '—', mono: true, fg: e.env ? undefined : 'var(--ink-40)' },
      { t: e.ip ?? '—', mono: true, fg: e.ip ? undefined : 'var(--ink-40)' },
      { t: e.userAgent ?? '—', fg: e.userAgent ? 'var(--ink-62)' : 'var(--ink-40)' },
    ],
  }));

  const inp = (label: string, node: ReactNode) => <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--ink-62)' }}>{label}{node}</label>;

  return (
    <>
      <Head title="Audit log" sub="Every user and admin action. Click a row for its metadata." />
      <Grid>
        <Cell span={12}>
          <Block title="Events" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 60} csv={csv} loading={l.loading && !l.data} error={describeErr(l.error) ? { ...describeErr(l.error)!, retry: l.reload } : null}
            search={{ value: q, placeholder: 'Action, actor, target or IP', onChange: setQ }}
            chips={[{ label: 'Staff only', on: f.staff === 'true', onClick: () => set({ staff: f.staff === 'true' ? '' : 'true' }) }, ...GROUPS.map((g) => ({ label: `${g}.*`, on: f.group === g, onClick: () => set({ group: f.group === g ? '' : g }) }))]}
            extra={<>
              <DateRange label="When" from={f.from} to={f.to} onChange={(from, to) => set({ from, to })} />
              {inp('Principal', <input className={`sel mono ${principal ? 'sel--on' : ''}`} style={{ width: mobile ? 180 : 300, fontSize: 11 }} value={principal} placeholder="Principal id, or any" spellCheck={false} onChange={(e) => setPrincipal(e.target.value)} />)}
              <SaveView />
            </>}
            empty={l.data && events.length === 0 ? 'No events match.' : undefined}
            foot={<ListMore list={l} noun="events" />}>
            <Table mobile={mobile} minWidth={1100} rows={rows} sort={s.sort} onSort={s.onSort}
              cols={[{ label: 'TIME', width: '120px', sort: 'at' }, { label: 'ACTION', width: 'minmax(170px,1.3fr)', sort: 'action', sortAsc: true }, { label: 'ACTOR', width: 'minmax(150px,1.2fr)', sort: 'actor', sortAsc: true }, { label: 'TARGET', width: 'minmax(140px,1.1fr)', sort: 'target', sortAsc: true }, { label: 'ENV', width: '80px', sort: 'env', sortAsc: true }, { label: 'IP', width: '110px', sort: 'ip', sortAsc: true }, { label: 'USER AGENT', width: 'minmax(140px,1fr)', sort: 'agent', sortAsc: true }]} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
